import React, { useEffect, useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan, faCheck, faPenToSquare, faXmark } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import List from "../../../components/List/List"
import { convertToLocalMoney } from "../../../Objects/Generics/Helps"
import { EPlanCouponDiscountType } from "../../../Enum/EPlanCouponDiscountType"
import { EPlanCouponApprovalStatus } from "../../../Enum/EPlanCouponApprovalStatus"
import { AdminPlan } from "../../../Objects/AdminPlan/AdminPlan"
import { AdminPlanCoupon } from "../../../Objects/AdminPlan/AdminPlanCoupon"
import { useAdminPlan } from "../../../hooks/AdminDashboard/useAdminPlan"
import { useAdminReseller } from "../../../hooks/AdminDashboard/useAdminReseller"
import { AdminReseller } from "../../../Objects/AdminReseller/AdminReseller"
import { AdminPlanCouponPopup } from "../AdminPlanCouponPopup"
import { AdminListCard, AdminListMetricItem } from "../components/AdminListCard"

const COUPON_FILTERS = [
  "admin_dashboard.plans_and_coupons.coupon_filter_active",
  "admin_dashboard.plans_and_coupons.coupon_filter_inactive",
  "admin_dashboard.plans_and_coupons.coupon_filter_all",
] as const

const formatDate = (value?: string) => {
  if (!value) return "-"

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) return "-"

  return date.toLocaleDateString("pt-BR")
}

const formatDiscount = (coupon: AdminPlanCoupon) =>
  coupon.discountType === EPlanCouponDiscountType.Percentage
    ? `${coupon.discountValue}%`
    : convertToLocalMoney(coupon.discountValue)

export const PlanCouponsSection: React.FC = () => {
  const { t } = useTranslation("frontend")

  const {
    handleGetAllPlans,
    handleGetAllCoupons,
    handleCreateCoupon,
    handleUpdateCoupon,
    handleDeactivateCoupon,
    handleApproveCoupon,
    handleRejectCoupon,
  } = useAdminPlan()

  const { handleGetAllResellers } = useAdminReseller()

  const [plans, setPlans] = useState<AdminPlan[]>([])

  const [coupons, setCoupons] = useState<AdminPlanCoupon[]>([])

  const [resellers, setResellers] = useState<AdminReseller[]>([])

  const [isCouponPopupOpen, setIsCouponPopupOpen] = useState(false)

  const [editingCoupon, setEditingCoupon] = useState<AdminPlanCoupon | null>(null)

  const refreshCoupons = () => handleGetAllCoupons(setCoupons)

  useEffect(() => {
    handleGetAllPlans(setPlans)
    handleGetAllResellers(setResellers)
    refreshCoupons()
  }, [])

  const planNameById = useMemo(() => new Map(plans.map((plan) => [plan.id, plan.name])), [plans])

  const resellerLabelById = useMemo(
    () => new Map(resellers.map((reseller) => [reseller.resellerId, `${reseller.name} (${reseller.referralCode})`])),
    [resellers],
  )

  const dropdownFilters = COUPON_FILTERS.map((key) => t(key))

  const handleAddCoupon = () => {
    setEditingCoupon(null)
    setIsCouponPopupOpen(true)
  }

  const handleEditCoupon = (id: number) => {
    const coupon = coupons.find((item) => item.id === id)

    if (!coupon) return

    setEditingCoupon(coupon)
    setIsCouponPopupOpen(true)
  }

  return (
    <div className="w-full overflow-auto bg-white p-1 md:p-0">
      <div className="p-2">
        <List
          messageCount={t("admin_dashboard.plans_and_coupons.coupons_count")}
          messageEmpty={t("admin_dashboard.plans_and_coupons.coupons_empty")}
          nameFindSearch={t("admin_dashboard.plans_and_coupons.coupons_search")}
          nameDropdown={dropdownFilters}
          handleAddButton={handleAddCoupon}
          handleRefreshButton={refreshCoupons}
          handleOnClick={handleEditCoupon}
          data={coupons.map((item, index) => {
            const isPending = item.approvalStatus === EPlanCouponApprovalStatus.Pending

            const isRejected = item.approvalStatus === EPlanCouponApprovalStatus.Rejected

            const status = isPending
              ? t("admin_dashboard.plans_and_coupons.coupon_filter_pending")
              : isRejected
                ? t("admin_dashboard.plans_and_coupons.coupon_filter_rejected")
                : item.isActive
                  ? t("admin_dashboard.plans_and_coupons.coupon_filter_active")
                  : t("admin_dashboard.plans_and_coupons.coupon_filter_inactive")

            const restrictedPlanName = item.restrictToPlanEntityId
              ? planNameById.get(item.restrictToPlanEntityId)
              : undefined

            const linkedResellerLabel = item.linkedResellerEntityId
              ? resellerLabelById.get(item.linkedResellerEntityId)
              : undefined

            return {
              id: item.id,
              index,
              title: item.code,
              linkImage: "",
              isDisable: isPending ? false : !item.isActive,
              dataFilter: [item.code, status, restrictedPlanName ?? "", linkedResellerLabel ?? ""],
              children: (
                <AdminListCard
                  title={item.code}
                  subtitle={formatDiscount(item)}
                  badge={status}
                  badgeClass={
                    isPending
                      ? "bg-amber-100 text-amber-700"
                      : isRejected
                        ? "bg-red-100 text-red-700"
                        : item.isActive
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-slate-100 text-slate-700"
                  }
                  cardClass={
                    isPending
                      ? "rounded-xl border-2 border-amber-200 bg-amber-50/40 p-3"
                      : isRejected
                        ? "rounded-xl border-2 border-red-200 bg-red-50/40 p-3"
                        : item.isActive
                          ? "rounded-xl border-2 border-emerald-200 bg-emerald-50/40 p-3"
                          : "rounded-xl border-2 border-slate-200 bg-slate-50 p-3"
                  }
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <AdminListMetricItem
                      label={t("admin_dashboard.plans_and_coupons.coupon_discount")}
                      value={formatDiscount(item)}
                      accent="text-orange-700"
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.plans_and_coupons.coupon_valid_period")}
                      value={`${formatDate(item.validFromUtc)} - ${formatDate(item.validUntilUtc)}`}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.plans_and_coupons.coupon_max_usage_total")}
                      value={item.maxUsageTotal ?? t("admin_dashboard.plans_and_coupons.coupon_unlimited")}
                    />

                    <AdminListMetricItem
                      label={t("admin_dashboard.plans_and_coupons.coupon_max_usage_per_company")}
                      value={item.maxUsagePerCompany ?? t("admin_dashboard.plans_and_coupons.coupon_unlimited")}
                    />
                  </div>

                  {(restrictedPlanName || item.restrictToPeriodType != null || item.restrictToCompanyEntityId) && (
                    <div className="rounded-lg border border-orange-100 bg-orange-50 px-3 py-2 text-xs text-slate-700">
                      <div className="flex flex-col gap-1">
                        {restrictedPlanName && (
                          <p>
                            <strong>{t("admin_dashboard.plans_and_coupons.coupon_restrict_to_plan")}:</strong>{" "}
                            {restrictedPlanName}
                          </p>
                        )}

                        {item.restrictToPeriodType != null && (
                          <p>
                            <strong>{t("admin_dashboard.plans_and_coupons.coupon_restrict_to_period")}:</strong>{" "}
                            {item.restrictToPeriodType.toEBillingPeriodTypeToString()}
                          </p>
                        )}

                        {item.restrictToCompanyEntityId && (
                          <p>
                            <strong>{t("admin_dashboard.plans_and_coupons.coupon_restrict_to_company")}:</strong> #
                            {item.restrictToCompanyEntityId}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {linkedResellerLabel && (
                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-slate-700">
                      <p>
                        <strong>{t("admin_dashboard.plans_and_coupons.coupon_commercial_referral_badge")}:</strong>{" "}
                        {linkedResellerLabel}
                      </p>
                    </div>
                  )}

                  {item.requestedByResellerEntityId && (
                    <div className="rounded-lg border border-amber-100 bg-amber-50 px-3 py-2 text-xs text-slate-700">
                      <p>
                        <strong>{t("admin_dashboard.plans_and_coupons.coupon_requested_by_reseller_badge")}</strong>
                      </p>

                      {isRejected && item.rejectionReason && (
                        <p className="mt-1 text-red-700">
                          {t("admin_dashboard.plans_and_coupons.coupon_rejection_reason")}: {item.rejectionReason}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-3">
                    {isPending ? (
                      <>
                        <button
                          className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-emerald-500 bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-emerald-700"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleApproveCoupon(item.id, refreshCoupons)
                          }}
                          data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.planCouponsSection.action.approveCoupon"
                        >
                          <FontAwesomeIcon icon={faCheck} />
                          <span>{t("admin_dashboard.plans_and_coupons.coupon_approve_action")}</span>
                        </button>

                        <button
                          className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-red-400 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 transition-colors hover:bg-red-100"
                          onClick={(event) => {
                            event.stopPropagation()

                            const reason = window.prompt(
                              t("admin_dashboard.plans_and_coupons.coupon_reject_prompt"),
                            )

                            if (reason === null) return

                            handleRejectCoupon(item.id, reason, refreshCoupons)
                          }}
                          data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.planCouponsSection.action.rejectCoupon"
                        >
                          <FontAwesomeIcon icon={faXmark} />
                          <span>{t("admin_dashboard.plans_and_coupons.coupon_reject_action")}</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-blue-500 bg-blue-600 px-3 py-1.5 text-xs font-bold text-white transition-colors hover:bg-blue-700"
                          onClick={(event) => {
                            event.stopPropagation()
                            handleEditCoupon(item.id)
                          }}
                          data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.planCouponsSection.action.editCoupon"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} />
                          <span>{t("admin_dashboard.plans_and_coupons.coupon_edit_action")}</span>
                        </button>

                        {item.isActive && (
                          <button
                            className="inline-flex min-h-[34px] items-center justify-center gap-1 rounded-lg border border-red-400 bg-red-50 px-3 py-1.5 text-xs font-bold text-red-700 transition-colors hover:bg-red-100"
                            onClick={(event) => {
                              event.stopPropagation()
                              handleDeactivateCoupon(item.id, refreshCoupons)
                            }}
                            data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.planCouponsSection.action.deactivateCoupon"
                          >
                            <FontAwesomeIcon icon={faBan} />
                            <span>{t("admin_dashboard.plans_and_coupons.coupon_deactivate_action")}</span>
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </AdminListCard>
              ),
            }
          })}
        />
      </div>

      {isCouponPopupOpen && (
        <AdminPlanCouponPopup
          coupon={editingCoupon}
          plans={plans}
          onClose={() => setIsCouponPopupOpen(false)}
          onSave={(couponId, request) => {
            const handleSuccess = () => {
              setIsCouponPopupOpen(false)
              refreshCoupons()
            }

            if (couponId) {
              handleUpdateCoupon(couponId, request, handleSuccess)

              return
            }

            handleCreateCoupon(request, handleSuccess)
          }}
        />
      )}
    </div>
  )
}
